import { useState, useEffect, useMemo, useCallback } from 'react';
import { Briefcase, DollarSign, Edit2, Plus, Trash2 } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { financeV2Service } from '../services/financeV2Service';
import { ServiceType } from '../types/financeV2.types';
import { DataTable } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { FormField } from '@/components/common/FormSection';
import { FinanceStatusBadge } from '@/components/finance-v2/FinanceBadges';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

function ErrorBox({ message }: { message: string }) {
  return (
    <p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{message}</p>
  );
}

export function FinanceV2ServiceTypesPage() {
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingServiceType, setEditingServiceType] = useState<ServiceType | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    hourly_rate: '',
    is_active: true
  });

  useEffect(() => {
    loadServiceTypes();
  }, []);

  const loadServiceTypes = async () => {
    try {
      setLoading(true);
      const response = await financeV2Service.serviceTypes.getAll();
      if (response.success) {
        setServiceTypes(response.data.service_types);
      }
    } catch (error) {
      console.error('Error loading service types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setFormError(null);
      const data = {
        name: formData.name,
        description: formData.description || undefined,
        hourly_rate: parseFloat(formData.hourly_rate),
        is_active: formData.is_active
      };

      if (editingServiceType) {
        await financeV2Service.serviceTypes.update(editingServiceType.id, data);
      } else {
        await financeV2Service.serviceTypes.create(data);
      }

      closeModal();
      loadServiceTypes();
    } catch (error) {
      console.error('Error saving service type:', error);
      setFormError('Erro ao salvar tipo de serviço');
    }
  };

  const handleEdit = (serviceType: ServiceType) => {
    setEditingServiceType(serviceType);
    setFormData({
      name: serviceType.name,
      description: serviceType.description || '',
      hourly_rate: serviceType.hourly_rate.toString(),
      is_active: serviceType.is_active
    });
    setFormError(null);
    setShowModal(true);
  };

  const handleDelete = useCallback(async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este tipo de serviço?')) {
      return;
    }

    try {
      setError(null);
      await financeV2Service.serviceTypes.delete(id);
      loadServiceTypes();
    } catch (error) {
      console.error('Error deleting service type:', error);
      setError('Erro ao excluir tipo de serviço');
    }
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      hourly_rate: '',
      is_active: true
    });
    setEditingServiceType(null);
    setFormError(null);
  };

  const openCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const columns = useMemo<ColumnDef<ServiceType>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Nome',
      cell: ({ row }) => <div className="text-sm font-medium">{row.original.name}</div>,
    },
    {
      accessorKey: 'description',
      header: 'Descrição',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="break-words text-sm text-muted-foreground">{row.original.description || '-'}</div>
      ),
    },
    {
      accessorKey: 'hourly_rate',
      header: 'Valor/Hora',
      cell: ({ row }) => (
        <div className="text-sm font-medium tabular-nums">{formatCurrency(row.original.hourly_rate)}</div>
      ),
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ row }) => <FinanceStatusBadge status={row.original.is_active ? 'Ativo' : 'Inativo'} />,
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">Ações</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="text-muted-foreground"
            onClick={() => handleEdit(row.original)}
            aria-label="Editar tipo de serviço"
          >
            <Edit2 className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-destructive"
            onClick={() => handleDelete(row.original.id)}
            aria-label="Excluir tipo de serviço"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      ),
    },
  ], [handleDelete]);

  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6" aria-busy="true">
        <span className="sr-only">Carregando...</span>
        <Skeleton className="h-8 w-56" />
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-12" />)}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <Briefcase className="size-6 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Tipos de Serviço</h1>
          </div>
          <p className="text-sm text-muted-foreground">Configure os tipos de serviço e suas taxas horárias</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="size-4" /> Novo Tipo de Serviço
        </Button>
      </div>

      {error && <ErrorBox message={error} />}

      {serviceTypes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-6 text-center">
            <DollarSign className="size-10 text-muted-foreground" aria-hidden="true" />
            <h3 className="text-base font-medium">Nenhum tipo de serviço cadastrado</h3>
            <p className="text-sm text-muted-foreground">Comece criando seu primeiro tipo de serviço para registrar horas</p>
            <Button className="mt-2" onClick={openCreate}>
              <Plus className="size-4" /> Criar Tipo de Serviço
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="min-w-0 py-0">
          <div className="min-w-0 p-2">
            <DataTable
              columns={columns}
              data={serviceTypes}
              minWidth="min-w-[640px]"
              emptyMessage="Nenhum tipo de serviço encontrado."
            />
          </div>
        </Card>
      )}

      <Dialog open={showModal} onOpenChange={(open) => { if (!open) closeModal() }}>
        <DialogContent fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{editingServiceType ? 'Editar Tipo de Serviço' : 'Novo Tipo de Serviço'}</DialogTitle>
          </DialogHeader>

          {formError && <ErrorBox message={formError} />}

          <form id="service-type-form" onSubmit={handleSubmit} className="flex min-w-0 flex-col gap-4">
            <FormField label="Nome *" htmlFor="service-type-name">
              <Input
                id="service-type-name"
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Consultoria Jurídica"
              />
            </FormField>

            <FormField label="Descrição" htmlFor="service-type-description">
              <Textarea
                id="service-type-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                placeholder="Descrição do serviço"
              />
            </FormField>

            <FormField label="Valor por Hora (R$) *" htmlFor="service-type-hourly-rate">
              <Input
                id="service-type-hourly-rate"
                type="number"
                required
                step="0.01"
                min="0"
                value={formData.hourly_rate}
                onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                placeholder="0.00"
              />
            </FormField>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="size-4 rounded-sm border-input accent-primary"
              />
              <Label htmlFor="is_active" className="font-normal">Ativo</Label>
            </div>
          </form>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeModal}>Cancelar</Button>
            <Button type="submit" form="service-type-form">{editingServiceType ? 'Salvar' : 'Criar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
